import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Download, X, Share, Plus, Sparkles, Bell, Zap, WifiOff } from "lucide-react";

/**
 * "Install Serpix" prompt.
 *
 * • Android / Desktop Chrome / Edge — listens for `beforeinstallprompt`, buffers
 *   it, then shows a custom pill. Click → `prompt.prompt()` → install.
 * • iOS Safari (which does not fire the event) — falls back to a small
 *   instructional card ("Share ⤴ → Add to Home Screen") shown once, dismissed
 *   for 30 days via localStorage.
 * • Hidden if the app is already running standalone (installed).
 * • Hidden on the /app/* routes to avoid distracting logged-in users mid-task.
 *
 * Position: bottom-right pill above the DmWidget bubble.
 */
const DISMISS_KEY = "serpix_install_dismissed_at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIosDevice = /iPhone|iPad|iPod/i.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return isIosDevice && isSafari;
}

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts && Date.now() - ts < DISMISS_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosCard, setShowIosCard] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setShowIosCard(false);
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: show the branded bottom-sheet tutorial after 12 s so the
    // user isn't ambushed the moment they land on the site.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setShowIosCard(true), 12000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = useCallback(async () => {
    if (!event) return;
    event.prompt();
    try {
      await event.userChoice;
    } finally {
      setEvent(null);
      setVisible(false);
    }
  }, [event]);

  const dismiss = useCallback(() => {
    setVisible(false);
    setShowIosCard(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  // Android / desktop prompt
  if (visible && event) {
    return (
      <div
        data-testid="pwa-install-prompt"
        className="fixed inset-x-3 bottom-3 z-40 flex items-center gap-3 rounded-2xl border border-violet-200 bg-white p-3 shadow-2xl shadow-violet-500/20 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px]"
      >
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-[#5B4CFB] via-[#7c5cf6] to-[#EC4899] text-white shadow-lg shadow-violet-500/25">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900">
            {t("pwa.android.title")}
          </div>
          <div className="truncate text-xs text-zinc-500">
            {t("pwa.android.subtitle")}
          </div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="flex-none rounded-full bg-zinc-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-zinc-800"
        >
          {t("pwa.android.install")}
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label={t("common.close")}
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // iOS Safari — branded bottom-sheet tutorial with visual instructions
  if (showIosCard) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={dismiss}
          data-testid="pwa-install-ios-backdrop"
        />
        {/* Bottom sheet */}
        <div
          data-testid="pwa-install-prompt-ios"
          className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-3xl border-t border-zinc-200 bg-white shadow-2xl shadow-black/25 animate-in slide-in-from-bottom-full duration-300 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md sm:rounded-3xl"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          {/* Drag handle (visual only) */}
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-zinc-300 sm:hidden" />

          <div className="px-6 pt-5">
            {/* Header */}
            <div className="flex items-start gap-3">
              <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-[#5B4CFB] via-[#7c5cf6] to-[#EC4899] shadow-xl shadow-violet-500/40">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-950">
                  {t("pwa.ios.title")}
                </h3>
                <p className="mt-0.5 text-sm text-zinc-600">
                  {t("pwa.ios.subtitle")}
                </p>
              </div>
              <button
                onClick={dismiss}
                data-testid="pwa-install-dismiss-ios"
                aria-label={t("common.close")}
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Benefits */}
            <div className="mt-5 grid grid-cols-3 gap-2">
              {[
                { icon: Zap, label: t("pwa.ios.benefit_fast") },
                { icon: Bell, label: t("pwa.ios.benefit_notify") },
                { icon: WifiOff, label: t("pwa.ios.benefit_offline") },
              ].map((b, i) => {
                const Ic = b.icon;
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-1.5 rounded-xl border border-zinc-100 bg-zinc-50/60 px-2 py-3 text-center"
                  >
                    <Ic className="h-4 w-4 text-violet-600" />
                    <span className="text-[10px] font-medium text-zinc-700">{b.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Steps */}
            <ol className="mt-5 space-y-2.5">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold text-violet-700">
                  1
                </span>
                <div className="flex-1 text-sm text-zinc-800">
                  <span className="mr-1.5 inline-flex items-center gap-1 rounded-md bg-zinc-100 px-1.5 py-0.5 font-mono text-xs">
                    <Share className="h-3.5 w-3.5" strokeWidth={2} />
                  </span>
                  {t("pwa.ios.step1")}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold text-violet-700">
                  2
                </span>
                <div className="flex-1 text-sm text-zinc-800">
                  <span className="mr-1.5 inline-flex items-center gap-1 rounded-md bg-zinc-100 px-1.5 py-0.5 text-xs font-medium">
                    <Plus className="h-3.5 w-3.5" strokeWidth={2} /> {t("pwa.ios.action_add")}
                  </span>
                  {t("pwa.ios.step2")}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold text-violet-700">
                  3
                </span>
                <div className="flex-1 text-sm text-zinc-800">
                  {t("pwa.ios.step3")}
                </div>
              </li>
            </ol>

            {/* CTA */}
            <button
              onClick={dismiss}
              data-testid="pwa-install-later-btn"
              className="mt-5 w-full rounded-full bg-zinc-950 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800"
            >
              {t("pwa.ios.got_it")}
            </button>
            <p className="mt-2 text-center text-[11px] text-zinc-500">
              {t("pwa.ios.remind_later")}
            </p>
          </div>
        </div>
      </>
    );
  }

  return null;
}
